import {
  isExecutionChainSource,
  type ExecutionChainReferenceCatalogV1,
  type OrgExecutionChainArtifactV1,
} from './org-execution-chain';
import { hasExactKeyCount, isNonEmptyText, isRecord } from './org-execution-chain-validation';

type SynergyStreamLink = OrgExecutionChainArtifactV1['synergyStreamLinks'][number];
type SynergyStream = ExecutionChainReferenceCatalogV1['synergyStreams'][number];

/** Raw shape guard for one synergy-stream link row, including its disposition variant. */
export function isSynergyStreamLinkRecord(value: unknown): value is Record<string, unknown> {
  if (
    !isRecord(value) ||
    !isExecutionChainSource(value) ||
    !isNonEmptyText(value.synergyStreamLinkId) ||
    !isNonEmptyText(value.workPackageId) ||
    !isNonEmptyText(value.synergyClaimId)
  ) {
    return false;
  }
  return value.aggregationDisposition === 'released_with_overlap_receipt'
    ? hasExactKeyCount(value, 7) && isNonEmptyText(value.overlapReceiptId)
    : hasExactKeyCount(value, 6) &&
        (value.aggregationDisposition === 'withheld_pending_overlap_receipts' ||
          value.aggregationDisposition === 'withdrawn_overlap_rejected');
}

/**
 * Validates each synergy-stream link against its own stream's overlap-receipt
 * status. A stream's disposition is judged independently of every other
 * stream: approving (or rejecting) one stream's receipt must never fail the
 * artifact over a different stream's link.
 */
export function validateSynergyStreamLinkReceipts(
  links: readonly SynergyStreamLink[],
  streamsById: ReadonlyMap<string, SynergyStream>,
  errors: string[]
): void {
  for (const link of links) {
    const stream = streamsById.get(link.synergyClaimId);
    if (!stream) {
      errors.push(`orphan synergy claim id: ${link.synergyClaimId}`);
      continue;
    }
    validateLinkAgainstReceiptStatus(link, stream, errors);
  }
}

function validateLinkAgainstReceiptStatus(
  link: SynergyStreamLink,
  stream: SynergyStream,
  errors: string[]
): void {
  switch (stream.overlapReceiptStatus) {
    case 'pending':
      if (link.aggregationDisposition !== 'withheld_pending_overlap_receipts') {
        errors.push(
          `synergy ${link.synergyClaimId} must remain withheld pending overlap receipts`
        );
      }
      return;
    case 'approved':
      if (link.aggregationDisposition !== 'released_with_overlap_receipt') {
        errors.push(`synergy ${link.synergyClaimId} must be released with its overlap receipt id`);
      }
      return;
    case 'rejected':
      if (link.aggregationDisposition !== 'withdrawn_overlap_rejected') {
        errors.push(`synergy ${link.synergyClaimId} must be withdrawn after overlap rejection`);
      }
      return;
    default:
      errors.push(`synergy ${link.synergyClaimId} has an unrecognized overlap receipt status`);
  }
}
